import React, { useEffect, useRef, useState } from 'react';

import c from './_fastTrackAnswerFeedback.module.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import i18n from 'i18n-react';

const FEEDBACK_DURATION = 10;

export interface FastTrackAnswerFeedbackProps {
  variant: 'correct' | 'incorrect';
  message: React.ReactNode;
  onNext: () => void;
}

export function FastTrackAnswerFeedback({ variant, message, onNext }: FastTrackAnswerFeedbackProps) {
  const [secondsLeft, setSecondsLeft] = useState(FEEDBACK_DURATION);
  // Guards against onNext firing twice (timer expiry racing a button click, or a
  // pending tick while react-pose keeps the exiting page mounted during the fade).
  const firedRef = useRef(false);
  const onNextRef = useRef(onNext);

  useEffect(() => {
    onNextRef.current = onNext;
  }, [onNext]);

  const handleNext = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    onNextRef.current();
  };

  useEffect(() => {
    if (secondsLeft <= 0) {
      handleNext();
      return undefined;
    }
    const id = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [secondsLeft]);

  const isCorrect = variant === 'correct';

  return (
    <div className={clsx(c.panel, isCorrect ? c.panelCorrect : c.panelIncorrect)} role="status">
      <p className={c.message}>
        <span className={c.iconBadge}>
          <FontAwesomeIcon icon={['fas', isCorrect ? 'check' : 'xmark']} />
        </span>
        {message}
      </p>
      <div className={c.timerRow}>
        <div className={c.timerTrack}>
          <div className={c.timerFill} style={{ animationDuration: `${FEEDBACK_DURATION}s` }} />
        </div>
        <span className={c.timerLabel}>{i18n.translate('FastTrackView.nextIn', { count: Math.max(secondsLeft, 0) })}</span>
      </div>
      <button type="button" className={c.nextButton} onClick={handleNext}>
        {i18n.translate('FastTrackView.goToNextQuestion')}
        <FontAwesomeIcon icon={['fas', 'arrow-right']} />
      </button>
    </div>
  );
}
